import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function RegenerateProposalButton({
  proposalId,
  eligible,
  disabledReason,
  hasManualCommercialOverrides,
  size = "sm",
  variant = "outline",
  className,
  onCompleted,
}: {
  proposalId: number;
  eligible: boolean;
  disabledReason?: string | null;
  hasManualCommercialOverrides?: boolean;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "outline" | "secondary" | "ghost";
  className?: string;
  onCompleted?: () => void | Promise<void>;
}) {
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const regenerate = trpc.proposals.regenerateImportedProposal.useMutation({
    onSuccess: async (result) => {
      await Promise.all([
        utils.proposals.getRetailDealOverview.invalidate({ id: proposalId }),
        utils.proposals.getProposalById.invalidate({ id: proposalId }),
        utils.proposals.listProposals.invalidate(),
        utils.proposals.listCustomerProposalCards.invalidate(),
      ]);
      toast.success("Proposal regenerated using latest parser");
      if (result.summaryDraftMode) {
        toast.warning("Parser used summary draft mode for this proposal.");
      }
      await onCompleted?.();
      setConfirmOpen(false);
    },
    onError: (error) => {
      if (error.data?.code === "CONFLICT") {
        setConfirmOpen(true);
        return;
      }
      toast.error(error.message || "Could not regenerate proposal.");
    },
  });

  const disabledHelp = String(disabledReason ?? "").trim();
  const visible = eligible || disabledHelp.length > 0;
  if (!visible || proposalId <= 0) return null;

  const runRegenerate = (acknowledgeManualOverwrite: boolean) => {
    regenerate.mutate({ proposalId, acknowledgeManualOverwrite });
  };

  const button = (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={className}
      disabled={!eligible || regenerate.isPending}
      onClick={() => {
        if (!eligible) return;
        if (hasManualCommercialOverrides) {
          setConfirmOpen(true);
          return;
        }
        runRegenerate(false);
      }}
    >
      <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${regenerate.isPending ? "animate-spin" : ""}`} />
      {regenerate.isPending ? "Regenerating…" : "Regenerate Proposal"}
    </Button>
  );

  return (
    <>
      {disabledHelp ? (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="inline-flex">{button}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            {disabledHelp}
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              Regeneration may overwrite manually edited line items. The original uploaded PDF will be re-parsed with
              the latest commercial interpretation engine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerate.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={regenerate.isPending}
              onClick={(event) => {
                event.preventDefault();
                runRegenerate(true);
              }}
            >
              Regenerate &amp; Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
